import React, { useState, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import QRCode from 'react-qr-code';

const PremiumHeader = ({ raffle, stats }) => {
  const [showQR, setShowQR] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const progressAnimation = useSpring({
    from: { width: '0%' },
    to: { width: `${((stats.sold + stats.reserved) / stats.total) * 100}%` },
    config: { duration: 1000 }
  });

  const numberAnimation = useSpring({
    from: { number: 0 },
    to: { 
      sold: stats.sold,
      reserved: stats.reserved,
      available: stats.available
    },
    config: { duration: 1500 }
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Por definir';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntilDraw = () => {
    if (!raffle.drawDate) return null;
    const drawDate = new Date(raffle.drawDate.seconds * 1000);
    const diff = drawDate - currentTime;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const daysUntilDraw = getDaysUntilDraw();

  return (
    <header className="relative overflow-hidden">
      {/* Premium Background with Animated Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900">
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        <div className="absolute inset-0 holographic-effect opacity-10"></div>
        
        {/* Animated Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 5}s`
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-12">
        {/* Top Section with Logo and QR */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
              <span className="text-2xl font-bold text-white">TR</span>
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-2xl">
                {raffle.title}
              </h1>
              <div className="flex items-center space-x-4 text-white/80">
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                  </svg>
                  {raffle.organizer}
                </span>
                {raffle.raffleNumber && (
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                    </svg>
                    RIFA #{raffle.raffleNumber}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* QR Code Button */}
          <button
            onClick={() => setShowQR(!showQR)}
            className="group relative bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all"
          >
            {showQR ? (
              <div className="bg-white p-2 rounded-lg">
                <QRCode value={window.location.href} size={120} />
              </div>
            ) : (
              <>
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2V5h1v1H5zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zm2 2v-1h1v1H5zM13 3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3zm1 2v1h1V5h-1z" clipRule="evenodd" />
                  <path d="M11 4a1 1 0 10-2 0v1a1 1 0 002 0V4zM10 7a1 1 0 011 1v1h2a1 1 0 110 2h-3a1 1 0 01-1-1V8a1 1 0 011-1zM16 9a1 1 0 100 2 1 1 0 000-2zM9 13a1 1 0 011-1h1a1 1 0 110 2v2a1 1 0 11-2 0v-3zM7 11a1 1 0 100-2H4a1 1 0 100 2h3zM17 13a1 1 0 01-1 1h-2a1 1 0 110-2h2a1 1 0 011 1zM16 17a1 1 0 100-2h-3a1 1 0 100 2h3z" />
                </svg>
                <span className="absolute -bottom-8 right-0 text-xs text-white/60 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                  Compartir QR
                </span>
              </>
            )}
          </button>
        </div>

        {/* Raffle Image if exists */}
        {raffle.imageUrl && (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
            <img 
              src={raffle.imageUrl} 
              alt={raffle.title}
              className="w-full h-48 md:h-64 object-cover"
            />
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Price Card */}
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Valor del boleto</span>
              <svg className="w-6 h-6 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="text-3xl font-bold">
              {formatCurrency(raffle.price)}
            </div>
          </div>

          {/* Total Tickets */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Total boletos</span>
              <svg className="w-6 h-6 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 00-2 2v6a2 2 0 002 2h2a1 1 0 100 2H6a4 4 0 01-4-4V5a4 4 0 014-4h5a4 4 0 014 4v10a4 4 0 01-4 4h-1a1 1 0 110-2h1a2 2 0 002-2V5a2 2 0 00-2-2h-1a1 1 0 110-2z" clipRule="evenodd" />
              </svg>
            </div>
            <animated.div className="text-3xl font-bold">
              {numberAnimation.sold.to(val => Math.floor(val) + stats.reserved + stats.available)}
            </animated.div>
          </div>

          {/* Sold Tickets */}
          <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Vendidos</span>
              <svg className="w-6 h-6 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <animated.div className="text-3xl font-bold">
              {numberAnimation.sold.to(val => Math.floor(val))}
            </animated.div>
          </div>

          {/* Available Tickets */}
          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Disponibles</span>
              <svg className="w-6 h-6 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
            </div>
            <animated.div className="text-3xl font-bold">
              {numberAnimation.available.to(val => Math.floor(val))}
            </animated.div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white/10 backdrop-blur-sm rounded-full p-2 mb-8">
          <div className="relative h-6 bg-black/20 rounded-full overflow-hidden">
            <animated.div 
              style={progressAnimation}
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-lg"
            >
              <div className="absolute inset-0 shimmer-effect opacity-50"></div>
            </animated.div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-semibold text-sm drop-shadow">
                {Math.round(((stats.sold + stats.reserved) / stats.total) * 100)}% vendido
              </span>
            </div>
          </div>
        </div>

        {/* Draw Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Draw Date */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 8h12v8H4V8z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-white/60 text-sm">Fecha del sorteo</p>
                <p className="text-white font-semibold">{formatDate(raffle.drawDate)}</p>
                {daysUntilDraw !== null && (
                  <p className="text-white/80 text-sm">
                    {daysUntilDraw === 0 ? '¡Hoy!' : `En ${daysUntilDraw} días`}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Lottery */}
          {raffle.lottery && (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/60 text-sm">Lotería</p>
                  <p className="text-white font-semibold">{raffle.lottery}</p>
                  {raffle.lotteryNumber && (
                    <p className="text-white/80 text-sm">Número: {raffle.lotteryNumber}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-white/60 text-sm">Métodos de pago</p>
                <p className="text-white font-semibold">
                  {raffle.paymentMethods?.filter(m => m.enabled).length || 0} disponibles
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Badge */}
        <div className="absolute top-4 right-4 hidden md:block">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg animate-pulse">
            ⭐ EXPERIENCIA PREMIUM ⭐
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" className="w-full h-12 md:h-20">
          <path 
            fill="rgba(255,255,255,0.1)" 
            d="M0,64L48,58.7C96,53,192,43,288,48C384,53,480,75,576,80C672,85,768,75,864,58.7C960,43,1056,21,1152,16C1248,11,1344,21,1392,26.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
          />
        </svg>
      </div>
    </header>
  );
};

export default PremiumHeader;